// Segment 5 — Prompt School
import { useState, useMemo } from 'react';
import { Copy, Check, Sparkles } from 'lucide-react';
import { toast } from 'sonner';

/* ── Teal-bordered callout ── */
const TealCallout = ({ label, children, className = '' }) => (
  <div
    data-testid="teal-callout"
    className={`rounded-xl bg-white border-l-4 border-brand-teal shadow-sm px-6 py-5 ${className}`}
  >
    {label && (
      <p className="text-xs font-bold uppercase tracking-[0.14em] text-brand-teal mb-1.5">{label}</p>
    )}
    <div className="text-ink leading-relaxed">{children}</div>
  </div>
);

/* ── Copyable output box ── */
const CopyBox = ({ text, placeholder, testId, buttonLabel = 'Copy prompt' }) => {
  const [copied, setCopied] = useState(false);
  const isEmpty = !text.trim();

  const handleCopy = async () => {
    if (isEmpty) return;
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      toast.success('Prompt copied — go paste it in Claude');
      setTimeout(() => setCopied(false), 1800);
    } catch {
      /* clipboard unavailable — no-op */
    }
  };

  return (
    <div data-testid={testId} className="rounded-2xl border border-black/10 bg-cream overflow-hidden">
      <pre className="px-5 py-4 text-sm text-ink leading-relaxed whitespace-pre-wrap font-sans min-h-[96px]">
        {isEmpty ? <span className="text-ink-muted">{placeholder}</span> : text}
      </pre>
      <div className="flex justify-end px-4 py-3 border-t border-black/[0.06] bg-white/50">
        <button
          onClick={handleCopy}
          disabled={isEmpty}
          data-testid={`${testId}-copy-btn`}
          className={`inline-flex items-center gap-2 px-6 py-3 rounded-full font-bold text-sm transition-colors ${
            isEmpty
              ? 'bg-black/5 text-ink-muted cursor-not-allowed'
              : copied
              ? 'bg-brand-teal text-white'
              : 'bg-brand-orange text-white hover:bg-brand-orange-hover'
          }`}
        >
          {copied ? <Check size={16} strokeWidth={2.5} /> : <Copy size={16} strokeWidth={2.2} />}
          {copied ? 'Copied!' : buttonLabel}
        </button>
      </div>
    </div>
  );
};

/* ── R-T-C-F tiles (placeholder copy) ── */
const RTCF = [
  { letter: 'R', name: 'Role',    def: 'Tell the AI who to be.',      example: 'You are a grants consultant who has written 100 successful CSR proposals for Indian NGOs.' },
  { letter: 'T', name: 'Task',    def: 'One clear, specific action.', example: "Draft the 'expected outcomes' section of our proposal, under 200 words." },
  { letter: 'C', name: 'Context', def: 'What it needs to know.',       example: "We run learning centres for 400 children in rural Karnataka; the funder is a bank's CSR committee; tone: warm but evidence-led." },
  { letter: 'F', name: 'Format',  def: 'The shape of the output.',     example: 'Three short paragraphs, no jargon, end with one measurable target.' },
];

const RTCFTile = ({ tile }) => (
  <div
    data-testid={`rtcf-tile-${tile.name.toLowerCase()}`}
    className="flex items-start gap-4 rounded-2xl bg-white border border-black/[0.07] shadow-sm p-5"
  >
    <span className="w-11 h-11 rounded-xl bg-brand-teal text-white font-bold text-xl flex items-center justify-center shrink-0">
      {tile.letter}
    </span>
    <div className="min-w-0">
      <p className="font-bold text-ink text-[15px]">
        {tile.letter} · {tile.name}
      </p>
      <p className="text-sm text-ink-secondary leading-snug mt-0.5">{tile.def}</p>
      <blockquote className="mt-2 border-l-2 border-brand-orange/40 pl-3 text-sm text-ink-muted italic leading-snug">
        {tile.example}
      </blockquote>
    </div>
  </div>
);

/* ── Labeled input ── */
const Field = ({ label, value, onChange, placeholder, testId, textarea }) => (
  <div>
    <label className="block text-sm font-semibold text-ink mb-1.5">{label}</label>
    {textarea ? (
      <textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        rows={2}
        data-testid={testId}
        className="w-full rounded-xl border border-black/10 bg-white px-4 py-3 text-sm text-ink placeholder:text-ink-muted focus:outline-none focus:border-brand-teal focus:ring-1 focus:ring-brand-teal/40 transition-colors resize-y"
      />
    ) : (
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        data-testid={testId}
        className="w-full rounded-xl border border-black/10 bg-white px-4 py-3 text-sm text-ink placeholder:text-ink-muted focus:outline-none focus:border-brand-teal focus:ring-1 focus:ring-brand-teal/40 transition-colors"
      />
    )}
  </div>
);

const SectionHeading = ({ chip, title }) => (
  <div className="mb-5">
    {chip && (
      <span className="inline-block text-xs font-bold uppercase tracking-[0.14em] text-brand-orange mb-2">
        {chip}
      </span>
    )}
    <h2 className="text-2xl font-bold text-ink leading-tight">{title}</h2>
  </div>
);

const Segment5 = () => {
  // Section 3 — R-T-C-F scratch prompt
  const [role, setRole] = useState('');
  const [task, setTask] = useState('');
  const [context, setContext] = useState('');
  const [format, setFormat] = useState('');

  const scratchPrompt = useMemo(() => {
    const parts = [];
    if (role.trim())    parts.push(`Role: You are ${role.trim()}.`);
    if (task.trim())    parts.push(`Task: ${task.trim()}.`);
    if (context.trim()) parts.push(`Context: ${context.trim()}.`);
    if (format.trim())  parts.push(`Format: ${format.trim()}.`);
    return parts.join('\n\n');
  }, [role, task, context, format]);

  // Section 4 — Policy scaffold
  const [orgName, setOrgName] = useState('');
  const [country, setCountry] = useState('');
  const [serve, setServe] = useState('');
  const [tools, setTools] = useState('');
  const [dataTypes, setDataTypes] = useState('');
  const [redLines, setRedLines] = useState('');
  const [policyPrompt, setPolicyPrompt] = useState('');

  const generatePolicyPrompt = () => {
    const f = (v) => (v.trim() ? v.trim() : '[not provided — please ask me]');
    const p = `You are an experienced advisor helping Indian nonprofit organizations write practical, responsible AI use policies. You know Indian law well — including the Digital Personal Data Protection Act 2023, the IT Act 2000 and IT Rules 2021, and child-protection law (POCSO Act 2012, Juvenile Justice Act 2015) where relevant. You write policies that small teams will actually read and follow: plain language, short sections, no legalese.

Draft the FIRST DRAFT of a responsible AI use policy for my organization, for internal leadership review — not final adoption.

About us:
- Organization & mission: ${f(orgName)}
- Country & registration: India. Laws we believe apply: ${f(country)}
- Who we serve: ${f(serve)}
- AI tools we use or plan to use: ${f(tools)}
- Kinds of data we handle: ${f(dataTypes)}
- Our non-negotiable red lines: ${f(redLines)}

Requirements:
1. Ground every rule in OUR jurisdiction (India) and OUR context above. Do not cite laws from other countries. If a law you'd expect isn't listed above, name it and flag it for our review rather than silently applying it.
2. Prioritize depth on two sections: (a) data rules — especially beneficiary data and, if we serve children, verifiable consent under DPDP Section 9; (b) prohibited uses.
3. Include: purpose · scope (who this covers) · approved tools & accounts · data rules · human oversight requirements (what must always be reviewed by a person) · prohibited uses · beneficiary dignity & imagery rules · incident & escalation path · roles (avoid concentrating all authority in one person) · review cadence.
4. Length: a complete short-form policy of 2–3 pages. If that's not possible with the information given, produce a full outline plus fully drafted "data rules" and "prohibited uses" sections, and list what you need from us to finish the rest.
5. Mark every assumption you make with [ASSUMPTION] so we can verify it.
6. End with three questions a board member would ask about this policy.`;
    setPolicyPrompt(p);
  };

  const critiqueQs = [
    'What did it assume about your jurisdiction?',
    'What did it miss about how you actually operate?',
    'What would embarrass you if leadership read it?',
  ];

  return (
    <div data-testid="segment-5">
      <h1 className="text-4xl md:text-5xl font-bold text-ink mb-3 leading-tight">Prompt School</h1>
      <p className="text-lg text-ink-secondary mb-10">
        Learn R-T-C-F, then draft your organization's AI policy.
      </p>

      <div className="space-y-14">

        {/* 1 — Story callout */}
        <TealCallout>
          <p className="text-lg font-semibold">One true story first — eyes up front.</p>
        </TealCallout>

        {/* 2 — R-T-C-F tiles */}
        <section data-testid="rtcf-section">
          <SectionHeading title="R-T-C-F" />
          <div className="grid sm:grid-cols-2 gap-4">
            {RTCF.map((t) => <RTCFTile key={t.name} tile={t} />)}
          </div>
        </section>

        {/* 3 — Challenge: Your Task */}
        <section data-testid="challenge-section">
          <SectionHeading chip="Challenge · 6 min" title="Your Task" />
          <div className="rounded-2xl bg-white border border-black/[0.07] shadow-sm p-6 mb-6">
            <p className="text-ink leading-relaxed">
              Take the task that eats your time. Write ONE full R-T-C-F prompt for it in your org's
              Claude workspace. Run it. Iterate once.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 gap-4 mb-5">
            <Field label="R · Role"    value={role}    onChange={setRole}    placeholder="You are a…"          testId="scratch-role" />
            <Field label="T · Task"    value={task}    onChange={setTask}    placeholder="Do this one thing…"  testId="scratch-task" />
            <Field label="C · Context" value={context} onChange={setContext} placeholder="The facts it needs…" testId="scratch-context" />
            <Field label="F · Format"  value={format}  onChange={setFormat}  placeholder="Shape of the output…" testId="scratch-format" />
          </div>

          <p className="text-xs text-ink-muted mb-3">Scratch space only — nothing is saved or sent anywhere.</p>
          <CopyBox
            text={scratchPrompt}
            placeholder="Your prompt builds here as you type…"
            testId="scratch-prompt-preview"
          />
        </section>

        {/* 4 — The Policy Draft */}
        <section data-testid="policy-section">
          <SectionHeading chip="15 min · key screen" title="The Policy Draft" />
          <div className="rounded-2xl bg-white border border-black/[0.07] shadow-sm p-6 space-y-5">
            <p className="font-bold text-ink text-lg">
              Your organization's Responsible AI Policy — intake scaffold
            </p>

            <Field label="Organization name & mission (one line)" value={orgName} onChange={setOrgName} placeholder="e.g. Roshni Foundation — education for out-of-school children" testId="policy-org" />

            <div>
              <Field label="Country & applicable laws" value={country} onChange={setCountry} placeholder="e.g. India" testId="policy-country" />
              <p className="text-xs text-ink-muted mt-1.5">
                India: DPDP Act 2023 · IT Rules 2021 · POCSO where children's data is involved
              </p>
            </div>

            <Field label="Who you serve (beneficiary profile)" value={serve} onChange={setServe} placeholder="e.g. children aged 6–14 in rural districts" testId="policy-serve" />
            <Field label="AI tools in use or planned" value={tools} onChange={setTools} placeholder="e.g. Claude, translation tools, chatbots" testId="policy-tools" />
            <Field label="Data you handle (types — no actual data!)" value={dataTypes} onChange={setDataTypes} placeholder="e.g. names, ages, attendance, health notes" testId="policy-data" textarea />
            <Field label="Non-negotiable red lines" value={redLines} onChange={setRedLines} placeholder="e.g. never upload beneficiary PII to public tools" testId="policy-redlines" textarea />

            <button
              onClick={generatePolicyPrompt}
              data-testid="policy-generate-btn"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-brand-teal text-white font-bold text-sm hover:bg-brand-teal/85 transition-colors"
            >
              <Sparkles size={16} strokeWidth={2.2} />
              Generate my prompt
            </button>

            {policyPrompt && (
              <div className="pt-1">
                <p className="text-sm font-semibold text-ink mb-2">Paste this into your org's Claude workspace.</p>
                <CopyBox text={policyPrompt} placeholder="" testId="policy-prompt-preview" buttonLabel="Copy prompt" />
              </div>
            )}
          </div>

          {/* Scope promise */}
          <div
            data-testid="scope-promise-card"
            className="mt-5 rounded-2xl bg-brand-teal/[0.06] border border-brand-teal/20 px-6 py-5"
          >
            <p className="text-xs font-bold uppercase tracking-[0.14em] text-brand-teal mb-1.5">Scope promise</p>
            <p className="text-sm text-ink leading-relaxed">
              In 15 minutes you'll have EITHER a complete short-form draft (2–3 pages) OR an outline
              plus two fully drafted sections (data rules, prohibited uses). Both are a win.
            </p>
          </div>
        </section>

        {/* 5 — Critique */}
        <section data-testid="critique-section">
          <SectionHeading chip="5 min" title="Critique" />
          <div className="grid sm:grid-cols-3 gap-4 mb-5">
            {critiqueQs.map((q, i) => (
              <div
                key={i}
                data-testid={`critique-card-${i + 1}`}
                className="rounded-2xl bg-white border border-black/[0.07] shadow-sm p-5"
              >
                <span className="block text-brand-orange font-bold text-2xl mb-2">{i + 1}</span>
                <p className="text-ink font-medium leading-snug">{q}</p>
              </div>
            ))}
          </div>
          <p className="text-ink-secondary leading-relaxed">
            Fix one thing. Then save it as a Project named{' '}
            <strong className="text-ink">"Responsible AI Policy — DRAFT"</strong>.
          </p>
        </section>

        {/* 6 — Footer callout */}
        <TealCallout label="Remember">
          <p className="font-medium">
            You're leaving with a first draft for internal review — not an adopted policy.
          </p>
        </TealCallout>
      </div>
    </div>
  );
};

export default Segment5;
